import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { createComponent } from '@carvana/merch-components';
import React, { forwardRef, useContext, useRef, useState } from 'react';
import Slider from 'react-slick';
import styles from '@/common/Carousel/Carousel.module.css';
import { CarouselSettings } from '~/components/common/Carousel/types';
import { useIsMobile } from '~/context/common/DeviceContext';
import { usePreventVerticalScroll } from '~/hooks/common';
import CarouselArrow from './CarouselArrow';
import Headings from './Headings';

export const IconWrapper = createComponent('div', {
  className: `flex h-full w-full items-center justify-center`
});

export const CarouselWrapper = createComponent('div', {
  className: styles.wrapper
});

type CarouselProps = React.PropsWithChildren<CarouselSettings> & {
  children?: React.ReactNode;
  dataQa?: string;
};

export default forwardRef<HTMLElement, CarouselProps>(
  function Carousel(props, ref) {
    const {
      arrows = true,
      centerMode = false,
      dataQa,
      children,
      className = '',
      dots = true,
      infinite = true,
      isSkeleton = false,
      heading,
      maxLength = 0,
      eventBaseName = '',
      sectionName = '',
      subHeading,
      slidesToShow = 4,
      slidesToScroll = 1,
      viewLinkUrl
    } = props;
    const { trackEvent } = useContext(AnalyticsContext);
    const [activeSlide, setActiveSlide] = useState(0);
    const sliderRef = useRef<Slider>(null);
    const sliderWrapperRef = useRef<HTMLElement>(null);
    const isMobile = useIsMobile();

    usePreventVerticalScroll(sliderWrapperRef);

    if (!maxLength) return null;

    const sliderSettings = {
      arrows,
      beforeChange: (_: number, next: number) => setActiveSlide(next),
      onSwipe: () => {
        trackEvent(`Swiped - ${eventBaseName}`, {
          description: 'Carousel Nav',
          sectionName: sectionName || heading
        });
      },
      centerMode,
      className,
      dots,
      infinite,
      ref: sliderRef,
      speed: 500,
      slidesToShow,
      slidesToScroll
    };

    const showHeading = Boolean(
      !isMobile || isSkeleton || heading || subHeading || viewLinkUrl
    );

    return (
      <div data-qa={dataQa}>
        {showHeading && (
          <article
            className="mb-16 flex w-full items-start justify-between m:mb-24 m:items-center"
            ref={ref}
          >
            <Headings {...props} />

            <div className="text-blue-6">
              <div
                className="hidden justify-between m:w-[100px] l:flex l:w-[112px] xl:hidden"
                data-qa="carousel-nav-top"
              >
                <CarouselArrow
                  activeSlide={activeSlide}
                  direction="left"
                  sliderRef={sliderRef}
                  {...props}
                />
                <CarouselArrow
                  activeSlide={activeSlide}
                  direction="right"
                  sliderRef={sliderRef}
                  {...props}
                />
              </div>
            </div>
          </article>
        )}

        <div className="relative" data-qa="carousel-nav-sides">
          <CarouselArrow
            activeSlide={activeSlide}
            direction="left"
            isAbsolute
            sliderRef={sliderRef}
            {...props}
          />

          <CarouselWrapper ref={sliderWrapperRef}>
            <Slider {...sliderSettings}>{children}</Slider>
          </CarouselWrapper>

          <CarouselArrow
            activeSlide={activeSlide}
            direction="right"
            isAbsolute
            sliderRef={sliderRef}
            {...props}
          />
        </div>
      </div>
    );
  }
);
